import { useCallback, useEffect, useState } from "react";
import { useI18n, type TranslationKey, serverError } from "../i18n";
import { AnimatePresence, motion } from "motion/react";
import {
  Ban,
  Check,
  Gift,
  Loader2,
  MailCheck,
  Search,
  ShieldOff,
  Undo2,
  X,
} from "lucide-react";

/**
 * 會員管理
 * ═══════════════════════════════════════════════════════════════
 *
 * 這一頁存在的理由是「不用下 SQL」。
 *
 * 客服補償、金流回呼掉了要補開會員、詐騙帳號要停用 ——
 * 這些都是真實會發生的事。沒有這個入口，唯一的做法是直接改資料庫，
 * 而直接改資料庫不會留下任何稽核紀錄，也沒有人會知道發生過。
 *
 * 所以三個會改變狀態的動作都經過 admin worker 寫稽核日誌，
 * 而且停用**強制填理由** —— 事後一定會有人問「為什麼」，
 * 「我記得好像是詐騙」不是一個可以拿出來的答案。
 */

interface Member {
  id: string;
  handle: string;
  email: string;
  displayName: string;
  role: string;
  market: string;
  emailVerifiedAt: string | null;
  suspendedAt: string | null;
  suspendedReason: string | null;
  memberSince: string;
  createdAt: string;
  membershipPlan: string | null;
  membershipFreeUntil: string | null;
  membershipPaidUntil: string | null;
  membershipCycle: string | null;
  membershipState: "trial" | "active" | "exhausted" | "expired" | "none";
  daysLeft: number;
}

/*
  exhausted 與 expired 的差別要留著：前者是開站優惠結束、
  從來沒付過錢，後者是付過錢但沒續約。招呼這兩種人的方式不一樣 ——
  一個是還沒說服過，一個是說服過但沒留住。
*/
/* 模組層存 key，不存文字 —— 求值的時候語系還不存在 */
const STATE_KEY: Record<Member["membershipState"], TranslationKey> = {
  trial: "mb.trial",
  active: "mb.active",
  exhausted: "mb.exhausted",
  expired: "mb.expired",
  none: "mb.none",
};

const FILTERS = [
  { value: "", key: "mb.all" },
  { value: "trial", key: "mb.trial" },
  { value: "active", key: "mb.paying" },
  { value: "exhausted", key: "mb.exhausted" },
  { value: "expired", key: "mb.expired" },
  { value: "unverified", key: "mb.unverified" },
  { value: "suspended", key: "mb.suspended" },
] as const;

export default function Members() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [data, setData] = useState<{ items: Member[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pageSize: "50" });
      if (q.trim()) params.set("q", q.trim());
      if (state) params.set("state", state);

      const res = await fetch(`/api/users?${params}`, { credentials: "same-origin" });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverError(payload, t, `HTTP ${res.status}`));
      setData(payload.data);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [q, state]);

  // 搜尋要 debounce —— 每打一個字就查一次，資料庫會被打爛
  useEffect(() => {
    const timer = setTimeout(() => void load(), q ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, q]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5 rounded-2xl nm-inset px-4 py-3">
          <Search className="size-4 shrink-0 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("mb.search")}
            className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-muted"
          />
          {q && (
            <button type="button" onClick={() => setQ("")} aria-label={t("jobs.clear")}>
              <X className="size-3.5 text-ink-muted" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setState(f.value)}
            className={
              "rounded-pill px-4 py-2 text-xs font-medium transition-all " +
              (state === f.value ? "nm-inset text-ink" : "nm-pressable text-ink-soft")
            }
          >
            {t(f.key)}
          </button>
        ))}
      </div>

      {error && (
        <p className="mt-5 rounded-card nm-inset p-5 text-sm text-brand-300">{error}</p>
      )}

      <p className="mt-5 text-xs text-ink-muted">
        {loading ? t("jobs.loading") : t("mb.total", { n: data?.total ?? 0 })}
      </p>

      <div className="mt-3 space-y-2.5">
        {loading && !data
          ? [0, 1, 2].map((i) => <div key={i} className="h-24 rounded-card shimmer" />)
          : (data?.items ?? []).map((member) => (
              <MemberRow key={member.id} member={member} onChange={load} />
            ))}

        {!loading && (data?.items ?? []).length === 0 && (
          <p className="rounded-card nm-inset p-8 text-center text-sm text-ink-muted">
            {t("mb.none2")}
          </p>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════

function MemberRow({ member, onChange }: { member: Member; onChange: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  async function post(action: string, body: unknown, key: string) {
    setBusy(key);
    setError(null);
    try {
      const res = await fetch(`/api/users/${member.id}/${action}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverError(payload, t, `HTTP ${res.status}`));
      setReason("");
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const suspended = Boolean(member.suspendedAt);

  return (
    <div className={"rounded-card p-5 " + (suspended ? "nm-inset" : "nm")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-ink">{member.displayName}</span>
            <span className="rounded-pill nm-inset-sm px-2 py-0.5 text-[10px] text-ink-muted">
              {member.market}
            </span>
            <span className="rounded-pill nm-inset-sm px-2 py-0.5 text-[10px] text-ink-muted">
              {member.role === "both"
                ? t("mb.roleBoth")
                : member.role === "client"
                  ? t("mb.roleClient")
                  : t("mb.roleFreelancer")}
            </span>
          </div>
          <p className="mt-1 font-mono text-[11px] text-ink-soft">{member.email}</p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {suspended && (
            <span className="flex items-center gap-1.5 rounded-pill bg-brand-600/26 px-2.5 py-1 text-[10px] font-medium text-brand-100">
              <Ban className="size-3" />
              {t("mb.suspended")}
            </span>
          )}
          {!member.emailVerifiedAt && (
            <span className="rounded-pill nm-inset-sm px-2.5 py-1 text-[10px] text-ink-muted">
              {t("mb.unverified")}
            </span>
          )}
          <span className="rounded-pill nm-sm px-2.5 py-1 text-[10px] font-medium text-ink-soft">
            {t(STATE_KEY[member.membershipState])}
            {member.daysLeft > 0 && ` · ${t("mb.daysLeft", { n: member.daysLeft })}`}
          </span>
        </div>
      </div>

      {suspended && member.suspendedReason && (
        <p className="mt-3 rounded-xl nm-sm px-3.5 py-2.5 text-[11px] leading-relaxed text-ink-soft">
          {t("mb.suspendReason", { reason: member.suspendedReason })}
        </p>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-4 rounded-xl nm-pressable px-4 py-2 text-xs font-medium text-ink-soft"
        aria-expanded={open}
      >
        {open ? t("jobs.collapse") : t("mb.manage")}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-4 space-y-4 rounded-2xl nm-inset p-4">
              {error && <p className="text-xs text-brand-300">{error}</p>}

              {/* 會員資格 */}
              {member.membershipState !== "none" || member.role !== "client" ? (
                <div>
                  <p className="text-[11px] font-medium text-ink">{t("mb.membership")}</p>
                  <p className="mt-1 text-[10px] leading-relaxed text-ink-muted">
                    {t("mb.grantNote")}
                  </p>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {[1, 3, 12].map((months) => (
                      <button
                        key={months}
                        type="button"
                        disabled={busy !== null}
                        onClick={() => void post("membership", { action: "grant_months", months }, `m${months}`)}
                        className="flex items-center gap-1.5 rounded-xl nm-pressable px-3.5 py-2 text-[11px] font-medium text-ink disabled:opacity-40"
                      >
                        {busy === `m${months}` ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          <Gift className="size-3" />
                        )}
                        {t("mb.grantMonths", { n: months })}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => void post("membership", { action: "revoke" }, "revoke")}
                      className="flex items-center gap-1.5 rounded-xl nm-pressable px-3.5 py-2 text-[11px] font-medium text-ink-soft disabled:opacity-40"
                    >
                      {busy === "revoke" ? (
                        <Loader2 className="size-3 animate-spin" />
                      ) : (
                        <Undo2 className="size-3" />
                      )}
                      {t("mb.revoke")}
                    </button>
                  </div>
                </div>
              ) : null}

              {/* 信箱驗證 */}
              {!member.emailVerifiedAt && (
                <div>
                  <p className="text-[11px] font-medium text-ink">{t("mb.emailVerify")}</p>
                  <p className="mt-1 text-[10px] leading-relaxed text-ink-muted">
                    {t("mb.emailVerifyNote")}
                  </p>
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => void post("verify-email", {}, "verify")}
                    className="mt-2.5 flex items-center gap-1.5 rounded-xl nm-pressable px-3.5 py-2 text-[11px] font-medium text-ink disabled:opacity-40"
                  >
                    {busy === "verify" ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <MailCheck className="size-3" />
                    )}
                    {t("mb.markVerified")}
                  </button>
                </div>
              )}

              {/* 停用 */}
              <div>
                <p className="text-[11px] font-medium text-ink">
                  {suspended ? t("mb.restore") : t("mb.suspend")}
                </p>
                <p className="mt-1 text-[10px] leading-relaxed text-ink-muted">
                  {suspended ? t("mb.restoreNote") : t("mb.suspendNote")}
                </p>

                {!suspended && (
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t("mb.suspendReasonPlaceholder")}
                    className="mt-2.5 w-full rounded-xl nm-inset-sm bg-transparent px-3.5 py-2.5 text-xs text-ink outline-none placeholder:text-ink-muted"
                  />
                )}

                <button
                  type="button"
                  disabled={busy !== null || (!suspended && !reason.trim())}
                  onClick={() =>
                    void post(
                      "suspend",
                      { suspended: !suspended, reason: reason.trim() || undefined },
                      "suspend",
                    )
                  }
                  className="mt-2.5 flex items-center gap-1.5 rounded-xl nm-pressable px-3.5 py-2 text-[11px] font-medium text-ink disabled:opacity-40"
                >
                  {busy === "suspend" ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : suspended ? (
                    <Check className="size-3" />
                  ) : (
                    <ShieldOff className="size-3" />
                  )}
                  {suspended ? t("mb.restore") : t("mb.suspendAction")}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
